import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/app/providers/AuthProvider';
import { errorMessage } from '@/lib/api/errors';
import { queryKeys } from '@/lib/query/keys';
import { ThemeToggle } from '@/ui';
import { HeroFeatureList } from './components/HeroFeatureList';
import { HeroNetworkIllustration } from './components/HeroNetworkIllustration';
import { HeroStatsBar } from './components/HeroStatsBar';
import { LeadGenLogo } from './components/LeadGenLogo';
import { LoginFormCard } from './components/LoginFormCard';
import { RequestAccessModal } from './components/RequestAccessModal';
import styles from './LoginPage.module.css';

export function LoginPage() {
  const { status, signIn, signOutReason } = useAuth();
  const location = useLocation();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isRequestAccessOpen, setIsRequestAccessOpen] = useState(false);
  // Validated on submit rather than by disabling the button. A disabled
  // control gives no reason, and the reason was usually not the user's -- an
  // autofilled field that never reached React looked empty to the old check.
  // See LoginFormCard's mount effect.
  const [emptyFieldError, setEmptyFieldError] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (credentials: { username: string; password: string }) =>
      signIn(credentials),
    onSuccess: () => {
      // START THE DASHBOARD'S FETCH BEFORE THE DASHBOARD EXISTS.
      //
      // Routes are code-split, so signing in used to run strictly in series:
      // navigate, download the dashboard chunk, mount it, and only then ask
      // for the analytics. The charts therefore arrived a visible beat after
      // the rest of the page.
      //
      // The token is set inside signIn before it resolves, so the request can
      // go out now and overlap the chunk download. By the time the dashboard
      // mounts the answer is usually already in the cache, and its useQuery
      // renders from it rather than fetching again -- staleTime there is five
      // minutes, comfortably longer than this gap.
      //
      // Fire-and-forget on purpose: this is an optimisation, and the
      // dashboard's own query remains the thing that actually guarantees the
      // data. A failure here costs nothing and must not block the redirect,
      // which is why nothing awaits it and the rejection is swallowed.
      // Imported dynamically, not at the top of the file. A static import
      // puts the dashboard's api module -- and what it pulls in -- into the
      // login bundle, which every visitor downloads before they can type a
      // password; measured, it cost 2 kB gzipped off the initial budget and
      // collapsed two lazy chunks into the entry. Deferring it keeps the
      // login page as small as it was, and this runs at the one moment the
      // dashboard is about to be needed anyway.
      void import('@/features/dashboard/api')
        .then(({ getDashboardAnalytics }) =>
          queryClient.prefetchQuery({
            queryKey: queryKeys.dashboard.analytics(),
            queryFn: ({ signal }) => getDashboardAnalytics(signal),
            staleTime: 5 * 60_000,
          }))
        .catch(() => undefined);
    },
  });

  if (status === 'authenticated') {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? '/'} replace />;
  }

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    // READ THE FORM, NOT THE STATE. The inputs are controlled, so state is
    // normally the same thing -- but only when every change reached React.
    // A browser or password manager fills a field by writing to the DOM, and
    // the synthetic change event React listens for does not reliably follow;
    // the field then looks filled while state is still ''.
    //
    // Submitting state in that situation sent empty credentials and failed as
    // though the password were wrong. The form element always holds what the
    // user can actually see, so it is what gets sent.
    const form = new FormData(event.currentTarget);
    // `get` is typed `string | File | null` -- a text input can only yield the
    // first, but the narrowing has to be written out.
    const field = (name: string): string => {
      const value = form.get(name);
      return typeof value === 'string' ? value : '';
    };
    const submittedUser = field('username').trim();
    const submittedPass = field('password');

    if (submittedUser === '' || submittedPass.trim() === '') {
      setEmptyFieldError('Enter your email address and password.');
      return;
    }
    setEmptyFieldError(null);
    // Keep state in step, so a failed attempt re-renders with the fields
    // still populated rather than clearing them.
    setUsername(submittedUser);
    setPassword(submittedPass);
    mutation.mutate({ username: submittedUser, password: submittedPass });
  };

  const reasonNotice =
    signOutReason === 'expired'
      ? 'Your session expired. Please sign in again.'
      : signOutReason === 'revoked'
        ? 'Your session is no longer valid. Please sign in again.'
        : null;

  // The empty-field message is shown first: it is the one the user can act on
  // immediately, and a stale server error beneath it would be confusing.
  const formError = emptyFieldError
    ?? (mutation.isError ? errorMessage(mutation.error) : null);

  return (
    <div className={styles.page}>
      {/* Accessible h1 heading for screen readers & test assertions */}
      <h1 className={styles.srOnly}>Sign in</h1>

      {/* ── Left Story Column ── */}
      <section className={styles.storyColumn} aria-label="LeadGen overview">
        {/* Brand Lockup */}
        <div className={styles.brandRow}>
          <LeadGenLogo size="lg" />
        </div>

        {/* Story Grid: Copy & Features on Left, 3D Illustration in the Mid */}
        <div className={styles.storyGrid}>
          <div className={styles.storyCopy}>
            <span className={styles.eyebrow}>Smarter business. Faster growth.</span>
            <h2 className={styles.heroTitle}>
              Powering modern businesses with intelligent workflows.
            </h2>
            <p className={styles.heroSubtitle}>
              Streamline your operations, collaborate with your team, and achieve more — all in
              one powerful platform.
            </p>

            <div className={styles.featuresWrapper}>
              <HeroFeatureList />
            </div>
          </div>

          {/* 3D Illustration centered vertically in the mid */}
          <div className={styles.illustrationSlot}>
            <HeroNetworkIllustration />
          </div>
        </div>

        {/* Bottom Trust & Metrics Bar */}
        <div className={styles.statsDivider} />
        <HeroStatsBar />
      </section>

      {/* ── Right Auth Column ── */}
      <section className={styles.authColumn} aria-label="Sign in to your account">
        {/* Top bar controls: Theme switcher */}
        <div className={styles.authTopBar}>
          <div className={styles.themeSlot}>
            <ThemeToggle />
          </div>
        </div>

        {/* Form Container centered in auth column */}
        <div className={styles.authFormCenter}>
          <LoginFormCard
            username={username}
            onUsernameChange={setUsername}
            password={password}
            onPasswordChange={setPassword}
            onSubmit={onSubmit}
            isPending={mutation.isPending}
            errorMessage={formError}
            reasonNotice={reasonNotice}
            onRequestAccess={() => setIsRequestAccessOpen(true)}
          />
        </div>
      </section>

      {/* Modal for Requesting Access */}
      <RequestAccessModal
        isOpen={isRequestAccessOpen}
        onClose={() => setIsRequestAccessOpen(false)}
      />
    </div>
  );
}
